// React-side coordinator for superuser "Login as" (impersonation).
//
// The token swap itself lives in lib/auth.ts (memory-only stash of the admin
// token + active-token swap). This hook is the ONLY place that orchestrates the
// full UX transaction so the banner, the api layer and the admin pages all
// agree on a single source of truth:
//
//   enter(userId):  POST /admin/users/{id}/impersonate → stash admin token,
//                   swap active token, publish target identity, clear the
//                   query cache, navigate to /dashboard.
//   exit():         restore admin token, clear target, clear cache, navigate
//                   back to /admin/users.
//
// State is a module-level store (NOT component state) because impersonation is
// process-global: it outlives any single component and must survive route
// changes. We expose it via useSyncExternalStore so the global banner and any
// page re-render in lockstep.

import { type ImpersonateOut, endImpersonationServer, impersonateUser } from '@/lib/admin';
import { IMPERSONATION_EXPIRED_EVENT } from '@/lib/api';
import {
  beginImpersonation,
  decodeJwtPayload,
  endImpersonation,
  isImpersonating,
} from '@/lib/auth';
import { queryClient } from '@/lib/query-client';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export interface ImpersonationTarget {
  id: string;
  email: string;
  display_name?: string | null;
  // Email of the admin behind the session (from the token `act` claim, if present).
  actorEmail?: string | null;
}

interface ImpersonationState {
  active: boolean;
  target: ImpersonationTarget | null;
}

let _state: ImpersonationState = { active: isImpersonating(), target: null };
const _subscribers = new Set<() => void>();

function emit() {
  for (const cb of _subscribers) cb();
}

function setState(next: ImpersonationState) {
  _state = next;
  emit();
}

function subscribe(cb: () => void): () => void {
  _subscribers.add(cb);
  return () => {
    _subscribers.delete(cb);
  };
}

function getSnapshot(): ImpersonationState {
  return _state;
}

function targetFromResponse(resp: ImpersonateOut): ImpersonationTarget {
  const payload = decodeJwtPayload(resp.access_token);
  const act = payload?.act;
  const actorEmail = typeof act === 'object' && act !== null ? (act.email ?? null) : null;
  return {
    id: resp.user.id,
    email: resp.user.email,
    display_name: resp.user.display_name,
    actorEmail,
  };
}

export function useImpersonation() {
  const state = useSyncExternalStore(subscribe, getSnapshot);
  const navigate = useNavigate();

  // The api interceptor restores the admin token synchronously on a 401 from an
  // expired impersonation token; here we finish the job in React-land: drop the
  // banner, flush the (now admin-scoped) cache, bounce to the admin list and
  // tell the user why.
  useEffect(() => {
    function onExpired() {
      setState({ active: false, target: null });
      queryClient.clear();
      toast.error('Impersonation session expired — back to your admin account.');
      navigate('/admin/users', { replace: true });
    }
    window.addEventListener(IMPERSONATION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(IMPERSONATION_EXPIRED_EVENT, onExpired);
  }, [navigate]);

  const enter = useCallback(
    async (userId: string) => {
      const resp = await impersonateUser(userId);
      beginImpersonation(resp.access_token);
      setState({ active: true, target: targetFromResponse(resp) });
      // Every cached query was fetched with the admin token — drop it all so the
      // whole app re-fetches as the target user (their /me, discoveries, etc.).
      queryClient.clear();
      navigate('/dashboard', { replace: true });
    },
    [navigate],
  );

  const exit = useCallback(async () => {
    // Revoke server-side FIRST, while the impersonation token is still the
    // active token (the /impersonation/end endpoint authenticates via its
    // `act` claim and kills its jti). Best-effort — an already-expired token
    // is moot, so we still fall through to the local cleanup on failure.
    try {
      await endImpersonationServer();
    } catch {
      /* token may already be expired/invalid — proceed to local cleanup */
    }
    endImpersonation();
    setState({ active: false, target: null });
    // Symmetric cache wipe so the admin doesn't briefly see the target's data.
    queryClient.clear();
    navigate('/admin/users', { replace: true });
  }, [navigate]);

  return {
    isImpersonating: state.active,
    target: state.target,
    enter,
    exit,
  };
}
